import React, { useEffect, useMemo } from 'react';
import FeatherIcon from 'src/components/10-atoms/icons/FeatherIcon';
import Column from 'src/components/10-atoms/layout/Column';
import Row from 'src/components/10-atoms/layout/Row';
import PageMeta from 'src/components/10-atoms/seo/PageMeta';
import Tab from 'src/components/10-atoms/tabs/Tab';
import TabHeader from 'src/components/10-atoms/tabs/TabHeader';
import Tabs from 'src/components/10-atoms/tabs/Tabs';
import { useListViewModeNavbarAction } from 'src/components/20-helper-views/ListViewModeToggle';
import Page from 'src/components/templates/page/Page.template';
import PageBody from 'src/components/templates/page/PageBody.template';
import PageHeader from 'src/components/templates/page/PageHeader.template';
import PageNavbar from 'src/components/templates/page/PageNavbar.template';
import { PageContentWidth } from 'src/constants/dimensions';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useListViewMode, { isQuestionListMasterDetail } from 'src/hooks/useListViewMode';
import useI18n from 'src/modules/translations/hooks/useI18n';
import GenericServerError from 'src/components/10-atoms/GenericServerError';
import QueryHandler from 'src/components/10-atoms/QueryHandler';
import useCallbackWithCtx from 'src/hooks/useCallbackUtils/useCallbackWithCtx';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import { isWeb } from 'src/utils/platform';
import EPBreadCrumbs from '../../components/EPBreadCrumbs';
import buildEPQuestionNavParams from '../../navigation/buildEPQuestionNavParams';
import getHeirarchyJsonLD from '../../seo/getHeirarchyJsonLD';
import analytics from './analytics';
import BookmarksTab from './container/BookmarksTab';
import DiagramsTab from './container/DiagramsTab';
import NotesTab from './container/NotesTab';
import QBankTab from './container/QBankTab';
import QPTab from './container/QPTab';
import useAddQuestionPaper from './container/QPTab/useAddQuestionPaper';
import TopicList from './container/TopicList';
import useData from './useData';
const Subject = ({ navigation, route }) => {
    const { subject, examLevel, subjectID } = route.params;
    const [state, actions] = useData(subjectID);
    const [{ styles, theme, deviceType }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    const { listViewMode } = useListViewMode();
    const listViewModeNavbarAction = useListViewModeNavbarAction();
    const addQuestionPaper = useAddQuestionPaper(subjectID);
    const isDesktopWeb = isWeb && deviceType.isDesktop;
    const activeTabID = state.tabs.tabs[state.tabs.activeIndex]?.id;
    const isQuestionListTab = activeTabID === 'QUESTION_BANK' || activeTabID === 'BOOKMARKS';
    const isMasterDetail = isQuestionListTab &&
        isQuestionListMasterDetail(deviceType.isDesktop, listViewMode);
    const navbarActions = useMemo(() => {
        const navActions = [
            isQuestionListTab && listViewModeNavbarAction,
            isDesktopWeb && {
                Icon: (props) => (React.createElement(FeatherIcon, { ...props, name: "plus", color: theme.palette.primary.dark })),
                title: translate('Question Paper'),
                onPress: addQuestionPaper,
            },
        ].filter(Boolean);
        return navActions.length ? navActions : undefined;
    }, [
        isQuestionListTab,
        listViewModeNavbarAction,
        translate,
        isDesktopWeb,
        theme.palette.primary.dark,
        addQuestionPaper,
    ]);
    useEffect(() => {
        if (!subject) {
            actions.subjectDetailsQuery.fetch();
        }
    }, []);
    const ancestorsDetails = state.subjectDetailsQuery.data?.ep_subjectDetails.ancestorsDetails;
    const subjectDetails = subject || state.subjectDetailsQuery.data?.ep_subjectDetails;
    const examLevelDetails = examLevel || ancestorsDetails?.examLevel;
    const community = ancestorsDetails?.community;
    const handleTopicPress = useCallbackWithCtx((ctx, topic) => {
        if (!ctx.subject) {
            return;
        }
        analytics.setWidget('TopicList').setTarget('Topic').click();
    }, {
        subject: subjectDetails,
        examLevel: examLevelDetails,
    });
    const handleQPPress = useCallbackWithCtx((ctx, questionPaper) => {
        analytics
            .setWidget('QuestionPaperList')
            .setTarget('QuestionPaper')
            .click();
    }, { examLevel: examLevelDetails, subject: subjectDetails });
    const buildQBankQuestionNavParams = useStableCallback((question, index) => buildEPQuestionNavParams({
        questionID: question.id,
        navigatedFrom: 'Q_BANK_QUESTIONS',
        navigationData: {
            sequence: index,
            filters: {
                ...route.params.filters,
                ancestorID: subjectID,
            },
        },
    }));
    const buildBookmarkQuestionNavParams = useStableCallback((question, index) => buildEPQuestionNavParams({
        questionID: question.id,
        navigatedFrom: 'Q_BANK_QUESTIONS',
        navigationData: {
            sequence: index,
            filters: {
                ancestorID: subjectID,
                bookmarked: true,
            },
        },
    }));
    const handleQBankQuestionPress = useStableCallback((question, index) => {
        analytics
            .setWidget('QBank_QuestionList')
            .setTarget('Question')
            .click();
    });
    const getQBankQuestionTo = useStableCallback((question, index) => ({
        screen: 'EP_Question',
        params: buildQBankQuestionNavParams(question, index),
    }));
    const handleBookmarkPress = useStableCallback((question, index) => {
        analytics
            .setWidget('Bookmarks_QuestionList')
            .setTarget('Question')
            .click();
    });
    const getBookmarkQuestionTo = useStableCallback((question, index) => ({
        screen: 'EP_Question',
        params: buildBookmarkQuestionNavParams(question, index),
    }));
    const handleDiagramRefQuestionPress = useStableCallback((question) => {
        analytics
            .setWidget('Diagram_refQuestionList')
            .setTarget('Question')
            .click();
        // navigation.navigate('EP_Question', {
        //   questionID: question.id,
        //   navigatedFrom: 'Q_BANK_QUESTIONS',
        //   navigationData: {
        //     sequence: index,
        //     filters: {
        //       ...route.params.filters,
        //       ancestorID: subjectID,
        //     }
        //   }
        // })
    });
    const breadCrumbsLoading = !examLevel && state.subjectDetailsQuery.loading;
    return (React.createElement(Page, { layout: isMasterDetail
            ? { contentMaxWidth: PageContentWidth.MASTER_DETAIL }
            : undefined },
        community && examLevelDetails && subjectDetails && (React.createElement(PageMeta, { title: `${subjectDetails.data.title} — COC Exam Questions`, description: `Previous question papers for the ${community.data.title} / ${examLevelDetails?.data.title} ${subjectDetails.data.title} Exam. Prepare year-wise questions or topic-wise questions. You can also bookmark questions and refer diagrams for preparation.`, canonical: `exam-prep/subject/${subjectID}`, jsonLd: {
                '@context': 'https://schema.org',
                '@graph': [
                    {
                        '@type': 'Course',
                        name: `${community.data.title} / ${examLevelDetails?.data.title} / ${subjectDetails.data.title}`,
                        description: `Previous question papers for the ${community.data.title} / ${examLevelDetails?.data.title} / ${subjectDetails.data.title} Exam. Prepare year-wise questions or topic-wise questions. You can also bookmark questions and refer diagrams for preparation.`,
                        provider: {
                            '@type': 'Organization',
                            name: 'MarineSamraj',
                            url: 'https://marinesamraj.com',
                        },
                        educationalLevel: 'Professional',
                        url: `https://marinesamraj.com/exam-prep/subject/${subjectID}`,
                    },
                    getHeirarchyJsonLD(ancestorsDetails, {
                        examLevel: examLevelDetails,
                        subject: subjectDetails,
                    }),
                ].filter(Boolean),
            } })),
        React.createElement(PageHeader, null,
            React.createElement(PageNavbar, { backType: "back", title: translate('Subject'), onBackPress: navigation.goBack, actions: navbarActions }),
            React.createElement(Column, { style: styles.header },
                React.createElement(Row, { alignSelf: 'stretch', yAxis: 'center', ph: theme.spacing.page },
                    React.createElement(EPBreadCrumbs, { loading: breadCrumbsLoading, visibleCount: 1, knownNodes: {
                            examLevel: examLevelDetails,
                            subject: subjectDetails,
                        }, ph: 0, style: { maxWidth: '100%' } })),
                React.createElement(TabHeader, { ph: theme.spacing.standard.xs, tabs: state.tabs.tabs, activeIndex: state.tabs.activeIndex, onChange: (index) => {
                        analytics
                            .setWidget('GroupBy')
                            .setTarget(state.tabs.tabs[index].id)
                            .click();
                        actions.tabs.setActiveIndex(index);
                    } }))),
        React.createElement(PageBody, null,
            React.createElement(QueryHandler, { skip: !!subjectDetails, queryState: state.subjectDetailsQuery, LoaderComponent: null, ErrorComponent: React.createElement(GenericServerError, { onRetry: actions.subjectDetailsQuery.fetch }), DataComponent: React.createElement(Tabs, { activeTab: state.tabs.tabs[state.tabs.activeIndex]?.id },
                    React.createElement(Tab, { id: "PAPERS" },
                        React.createElement(QPTab, { subject: subjectDetails, subjectID: subjectID, onQPPress: handleQPPress, onAddQuestionPaper: addQuestionPaper })),
                    React.createElement(Tab, { id: "TOPICS" },
                        React.createElement(TopicList, { subjectID: subjectID, onTopicPress: handleTopicPress })),
                    React.createElement(Tab, { id: "NOTES" },
                        React.createElement(NotesTab, { subjectID: subjectID })),
                    React.createElement(Tab, { id: "BOOKMARKS" },
                        React.createElement(BookmarksTab, { subjectID: subjectID, onQuestionPress: handleBookmarkPress, getQuestionTo: getBookmarkQuestionTo })),
                    React.createElement(Tab, { id: "DIAGRAMS" },
                        React.createElement(DiagramsTab, { subjectID: subjectID, onQuestionPress: handleDiagramRefQuestionPress })),
                    React.createElement(Tab, { id: "QUESTION_BANK" },
                        React.createElement(QBankTab, { subjectID: subjectID, onQuestionPress: handleQBankQuestionPress, getQuestionTo: getQBankQuestionTo }))) }))));
};
const stylesGenerator = createThemedStyle(({ theme }) => ({
    header: {
        backgroundColor: theme.palette.background.main,
    },
}));
export default Subject;
